import { Injectable, inject } from '@angular/core';
import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, catchError, switchMap } from 'rxjs';
import { AuthService } from './auth.service';
import { AdminCompanyContextService } from '@services/admin-company-context.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly authService = inject(AuthService);
    private readonly adminCompanyContextService = inject(
        AdminCompanyContextService,
    );

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<HttpEvent<any>> {
        const authReq = this.withContextHeaders(
            req,
            this.authService.getToken(),
        );

        return next.handle(authReq).pipe(
            catchError((err: HttpErrorResponse) => {
                // Для самих auth-запросов (login/refresh) 401 — окончательный ответ, обновлять токен нечем.
                if (err.status !== 401 || req.url.includes('/auth/')) {
                    return throwError(() => err);
                }

                return this.authService.refreshAccessToken().pipe(
                    catchError((refreshErr: unknown) => {
                        // Сетевой сбой (status 0) не говорит о невалидной сессии — не разлогиниваем,
                        // любая другая ошибка refresh означает, что сессию восстановить нельзя.
                        const isNetworkError =
                            refreshErr instanceof HttpErrorResponse &&
                            refreshErr.status === 0;
                        if (!isNetworkError) {
                            this.authService.logout();
                        }
                        return throwError(() => err);
                    }),
                    switchMap(({ accessToken }) =>
                        next.handle(this.withContextHeaders(req, accessToken)),
                    ),
                );
            }),
        );
    }

    private withContextHeaders(
        req: HttpRequest<any>,
        token: string | null,
    ): HttpRequest<any> {
        let headers = req.headers;

        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
        }

        const companyId = this.adminCompanyContextService.companyId();
        if (companyId) {
            headers = headers.set('x-company-id', String(companyId));
        }

        return req.clone({ headers });
    }
}
